import React from 'react';
import { Box, Skeleton } from '@mui/material';
import type { SkeletonProps, BoxProps } from '@mui/material';

type Props = {
  variant?: 'text' | 'card' | 'list' | 'grid' | 'custom';
  lines?: number;
  height?: number | string;
  width?: number | string;
  animation?: SkeletonProps['animation'];
  sx?: BoxProps['sx'];
  children?: React.ReactNode;
};

export const LoadingSkeleton: React.FC<Props> = ({
  variant = 'text',
  lines = 3,
  height,
  width,
  animation = 'pulse',
  sx = {},
  children
}) => {
  if (children) {
    return <Box sx={sx}>{children}</Box>;
  }

  switch (variant) {
    case 'text':
      return (
        <Box sx={sx}>
          {Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={width || (index === lines - 1 ? '60%' : '100%')}
              height={height || 24}
              animation={animation}
              sx={{ mb: index < lines - 1 ? 0.5 : 0 }}
            />
          ))}
        </Box>
      );

    case 'card':
      return (
        <Box sx={sx}>
          <Skeleton
            variant="text"
            width="30%"
            height={32}
            animation={animation}
          />
          <Skeleton
            variant="rectangular"
            height={height || 120}
            width={width}
            animation={animation}
            sx={{ mt: 1, borderRadius: 1 }}
          />
        </Box>
      );

    case 'list':
      return (
        <Box sx={sx}>
          {Array.from({ length: lines }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                animation={animation}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" animation={animation} />
                <Skeleton variant="text" width="40%" animation={animation} />
              </Box>
            </Box>
          ))}
        </Box>
      );

    case 'grid':
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
            ...sx
          }}
        >
          {Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={height || 150}
              animation={animation}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      );

    case 'custom':
    default:
      return (
        <Skeleton
          variant="rectangular"
          width={width}
          height={height || 100}
          animation={animation}
          sx={sx}
        />
      );
  }
};
